import React from "react";
import { documentDate } from "../lib/signatures";

const rows = [
  { key: "bulan_lalu", label: "Jumlah Penduduk s/d Bulan Lalu" },
  { key: "lahir",      label: "Kelahiran Bulan Ini" },
  { key: "meninggal",  label: "Kematian Bulan Ini" },
  { key: "pindah",     label: "Pindah Bulan Ini" },
  { key: "datang",     label: "Datang Bulan Ini" },
  { key: "akhir",      label: "Jumlah Penduduk Akhir Bulan Ini" },
];

const groups = [
  { key: "tetap" },
  { key: "bandung" },
  { key: "jabar" },
  { key: "luar_jabar" },
];

function cellVal(cellData) {
  if (!cellData) return { l: 0, p: 0, jumlah: 0 };
  const l = Number(cellData.l) || 0;
  const p = Number(cellData.p) || 0;
  const jumlah = cellData.jumlah != null ? Number(cellData.jumlah) : l + p;
  return { l, p, jumlah };
}

/**
 * Unified A.5 document renderer — used by:
 *  - Buku Matriks A.5 (mode preview di layar)
 *  - Detail A.5
 *  - Cetak A.5 (kelurahan / rw / rt)
 *
 * Props:
 *  - data: object with { kelompok: { tetap, bandung, jabar, luar_jabar } }
 *  - periode: "September 2026" (label)
 *  - rw, rt: e.g. "01" / "001" OR "SELURUH RW"
 *  - kelurahan: default "Suka Asih"
 *  - scope: "kelurahan" | "rw" | "rt"
 *  - signature: { left, right } each { title, subtitle, nama, url_ttd, url_stempel }
 */
export default function A5Matrix({
  data,
  periode = "-",
  rw = "-",
  rt = "-",
  kelurahan = "Suka Asih",
  scope = "rt",
  signature,
  bulan,
  tahun,
  showSignature = false,
}) {
  const k = data?.kelompok || {};

  const rtLabel = scope === "kelurahan" ? "SELURUH RW"
                : scope === "rw" ? "SELURUH RT"
                : `${rt} / ${rw}`;

  // Default signature blocks based on scope
  const defaultLeft = scope === "rt"
    ? { title: "Mengetahui,", subtitle: `Ketua RW ${rw}`, nama: "................" }
    : { title: "Mengetahui,", subtitle: `Lurah ${kelurahan}`, nama: "................" };
  const signatureDate = documentDate(bulan, tahun);
  const defaultRight = scope === "rt"
    ? { title: signatureDate, subtitle: `Ketua RT ${rt}`, nama: "................" }
    : scope === "rw"
      ? { title: signatureDate, subtitle: `Ketua RW ${rw}`, nama: "................" }
      : { title: signatureDate, subtitle: `Lurah ${kelurahan}`, nama: "................" };

  const sigLeft = { ...defaultLeft, ...(signature?.left || {}) };
  const sigRight = { ...defaultRight, ...(signature?.right || {}), title: signatureDate };

  return (
    <div className="a5-doc fade-in" data-testid="a5-matrix">
      {/* Header */}
      <div className="a5-header">
        <div className="a5-title">LAPORAN KEPENDUDUKAN</div>
        <div className="a5-title-2">(FORMAT A.5)</div>
        <div className="a5-title mt-2">BUKU PERKEMBANGAN PENDUDUK</div>
        <div className="a5-title-2">(RT/RW)</div>
      </div>

      {/* Meta rows */}
      <div className="a5-meta-grid">
        <div className="a5-meta-item">
          <span className="a5-meta-label">Bulan / Tahun</span>
          <span className="a5-meta-colon">:</span>
          <span className="a5-meta-value">{(periode || "-").toUpperCase()}</span>
        </div>
        <div className="a5-meta-item">
          <span className="a5-meta-label">RT / RW</span>
          <span className="a5-meta-colon">:</span>
          <span className="a5-meta-value">{rtLabel}</span>
        </div>
        <div />
        <div className="a5-meta-item">
          <span className="a5-meta-label">Kelurahan</span>
          <span className="a5-meta-colon">:</span>
          <span className="a5-meta-value">{kelurahan}</span>
        </div>
      </div>

      {/* Table */}
      <div className="a5-table-scroll">
        <table className="a5-table-v2">
          <thead>
            <tr>
              <th rowSpan={3} className="col-no">NO.</th>
              <th rowSpan={3} className="col-label">PERINCIAN JUMLAH</th>
              <th colSpan={3}>PENDUDUK TETAP</th>
              <th colSpan={9}>PENDUDUK SEMENTARA / MUSIMAN</th>
              <th colSpan={3}>JUMLAH SELURUHNYA</th>
            </tr>
            <tr>
              <th rowSpan={2}>L</th>
              <th rowSpan={2}>P</th>
              <th rowSpan={2}>JUMLAH</th>
              <th colSpan={3}>KOTA BANDUNG</th>
              <th colSpan={3}>WILAYAH JAWA BARAT</th>
              <th colSpan={3}>LUAR JAWA BARAT</th>
              <th rowSpan={2}>L</th>
              <th rowSpan={2}>P</th>
              <th rowSpan={2}>JUMLAH</th>
            </tr>
            <tr>
              <th>L</th><th>P</th><th>JML</th>
              <th>L</th><th>P</th><th>JML</th>
              <th>L</th><th>P</th><th>JML</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const cells = groups.map((g) => cellVal(k[g.key]?.[row.key]));
              const totalL = cells.reduce((s, c) => s + c.l, 0);
              const totalP = cells.reduce((s, c) => s + c.p, 0);
              const totalJ = totalL + totalP;
              const isSum = row.key === "akhir";
              return (
                <tr key={row.key} className={isSum ? "row-sum" : ""}>
                  <td className="num">{idx + 1}</td>
                  <td className="row-label">{row.label}</td>
                  {cells.map((c, i) => (
                    <React.Fragment key={groups[i].key}>
                      <td className="num">{c.l}</td>
                      <td className="num">{c.p}</td>
                      <td className="num jml">{c.jumlah}</td>
                    </React.Fragment>
                  ))}
                  <td className="num">{totalL}</td>
                  <td className="num">{totalP}</td>
                  <td className="num jml">{totalJ}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Signature block — only shown for print/cetak */}
      {showSignature && (
        <div className="a5-sign-grid">
          {scope !== "kelurahan" && (
            <div className="a5-sign-col">
              <div>{sigLeft.title}</div>
              <div className="a5-sign-role">{sigLeft.subtitle}</div>
              <div className="a5-sign-space">
                {sigLeft.url_ttd && <img src={sigLeft.url_ttd} alt="TTD" className="a5-sign-img" />}
                {sigLeft.url_stempel && <img src={sigLeft.url_stempel} alt="Stempel" className="a5-sign-stamp" />}
              </div>
              <div className="a5-sign-name">{sigLeft.nama || "................"}</div>
              {sigLeft.nip && <div className="a5-sign-nip">NIP. {sigLeft.nip}</div>}
            </div>
          )}
          <div className="a5-sign-col" style={scope === "kelurahan" ? { gridColumn: "2" } : undefined}>
            <div>{sigRight.title}</div>
            <div className="a5-sign-role">{sigRight.subtitle}</div>
            <div className="a5-sign-space">
              {sigRight.url_ttd && <img src={sigRight.url_ttd} alt="TTD" className="a5-sign-img" />}
              {sigRight.url_stempel && <img src={sigRight.url_stempel} alt="Stempel" className="a5-sign-stamp" />}
            </div>
            <div className="a5-sign-name">{sigRight.nama || "................"}</div>
            {sigRight.nip && <div className="a5-sign-nip">NIP. {sigRight.nip}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
